import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field, RootError, Section } from '@/components/ui/form-controls';
import { Input } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { prettyJson } from '@/lib/json';
import { tenantKeys, useJdeCredentials, useTenantMutation, type JdeVerifyResponse, type TenantDto } from './api';
import { jdeKeyCredentialsSchema, type JdeKeyCredentialsForm } from './schemas';
import './pilotI18n';

type JdeKeyPairResponse = components['schemas']['JdeKeyPairResponse'];

/**
 * private_key_jwt onboarding (ADR 0021): generate an EC P-256 key pair (only the public JWK leaves the platform), hand
 * the JWK to the jde-napi admin, then enter the client id + kid they return and verify. A new pair (rotation) stays
 * pending until it verifies.
 */
export function JdeKeySection({ tenant, onVerified }: { tenant: TenantDto; onVerified: (r: JdeVerifyResponse) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const creds = useJdeCredentials(tenant.id);
  const [generated, setGenerated] = useState<JdeKeyPairResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const generate = useTenantMutation(tenant.id, async () =>
    unwrap(await api.POST('/v1/tenants/{id}/jde-credentials/keypair', { params: { path: { id: tenant.id } } })) as JdeKeyPairResponse,
  );

  const form = useForm<JdeKeyCredentialsForm>({
    resolver: zodResolver(jdeKeyCredentialsSchema(t)),
    values: { clientId: creds.data?.clientId ?? tenant.jde.clientId ?? '', keyId: creds.data?.keyId ?? '' },
  });
  const save = useTenantMutation(tenant.id, async (v: JdeKeyCredentialsForm) =>
    unwrap(
      await api.PUT('/v1/tenants/{id}/jde-credentials', {
        params: { path: { id: tenant.id } },
        body: { clientId: v.clientId.trim(), clientSecret: null, keyId: v.keyId.trim() },
      }),
    ),
  );

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      onVerified(await save.mutateAsync(v));
      setGenerated(null);
      await qc.invalidateQueries({ queryKey: tenantKeys.part(tenant.id, 'jde-credentials') });
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['clientId', 'keyId'] });
    }
  });

  const pendingJwk = generated?.publicJwk ?? creds.data?.pendingKey?.publicJwk;
  const pendingThumb = generated?.thumbprint ?? creds.data?.pendingKey?.thumbprint;
  const { errors, isSubmitting } = form.formState;

  return (
    <Section
      title={t('pilot.keys.title')}
      description={t('pilot.keys.description')}
      actions={
        <Button
          variant="outline"
          size="sm"
          disabled={generate.isPending}
          onClick={() =>
            void generate.mutateAsync(undefined).then(async (r) => {
              setGenerated(r);
              setCopied(false);
              await qc.invalidateQueries({ queryKey: tenantKeys.part(tenant.id, 'jde-credentials') });
            })
          }
        >
          {creds.data?.hasKey ? t('pilot.keys.regenerate') : t('pilot.keys.generate')}
        </Button>
      }
    >
      {creds.data?.mode && <div className="text-xs text-muted-foreground">{t('pilot.keys.mode', { mode: creds.data.mode })}</div>}

      {creds.data?.activeKey ? (
        <div className="text-xs">
          <Badge variant="success">{t('pilot.keys.active')}</Badge>{' '}
          <span className="font-mono text-muted-foreground">{t('pilot.keys.thumbprint', { value: creds.data.activeKey.thumbprint })}</span>
        </div>
      ) : (
        !pendingJwk && <p className="text-xs text-muted-foreground">{t('pilot.keys.noKey')}</p>
      )}

      {pendingJwk && (
        <div className="flex flex-col gap-2 rounded-md border border-dashed p-3" data-testid="pending-jwk">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-medium">
              <Badge variant="warning">{t('pilot.keys.pending')}</Badge> {t('pilot.keys.publicJwk')}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(prettyJson(pendingJwk)).then(() => setCopied(true));
              }}
            >
              {copied ? t('pilot.keys.copied') : t('pilot.keys.copy')}
            </Button>
          </div>
          <pre className="overflow-auto rounded bg-muted/30 p-2 font-mono text-xs">{prettyJson(pendingJwk)}</pre>
          <div className="font-mono text-xs text-muted-foreground">{t('pilot.keys.thumbprint', { value: pendingThumb ?? '' })}</div>
          <p className="text-xs text-muted-foreground">{t('pilot.keys.instructions')}</p>
        </div>
      )}

      <form className="grid max-w-xl gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
        <Field label={t('admin.jde.clientId')} htmlFor="jde-key-client" error={errors.clientId?.message}>
          <Input id="jde-key-client" className="font-mono" aria-invalid={!!errors.clientId} {...form.register('clientId')} />
        </Field>
        <Field label={t('pilot.keys.keyId')} htmlFor="jde-key-id" error={errors.keyId?.message}>
          <Input id="jde-key-id" className="font-mono" aria-invalid={!!errors.keyId} {...form.register('keyId')} />
        </Field>
        <RootError message={errors.root?.message} />
        <div>
          <Button type="submit" disabled={isSubmitting || (!pendingJwk && !creds.data?.hasKey)}>
            {t('admin.jde.saveAndVerify')}
          </Button>
        </div>
      </form>
    </Section>
  );
}
